import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { SxProps, useTheme } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import UploadGrid from 'pages/uploadPage/components/uploadGrid';
import map from 'lodash-es/map';
import { each } from 'lodash-es';
import CreateFileController from 'widgets/files/createFileController';
import { useTranslation } from 'react-i18next';
import IconFolder from 'shared/icons/iconFolder';
import UploadFolderArea from 'shared/lib/components/controls/uploadArea/uploadFolderArea';
import { filesApi } from 'entities/file/api/filesApi';
import { UploadedFileWithPreview } from 'pages/uploadPage/uploadFiles/uploadFiles';
import FolderNodeComponent from 'pages/uploadPage/uploadFolder/tree/folderNode';
import { FolderNodeModel } from 'pages/uploadPage/uploadFolder/tree/folderNodeModel';
import TreeContainer from 'pages/uploadPage/uploadFolder/tree/treeContainer';
import { BackErrors } from 'shared/lib/utils/globalUtils';
import { nbsp } from 'shared/lib/utils/uiConstants';
import { InitialPlacement } from 'entities/types';
import { isFileIgnored } from 'shared/lib/components/controls/uploadArea/components/utils';

export default observer(function UploadFolder({
  isVisible,
  initialSectionId,
  initialFolderId,
  initialName,
}: {
  isVisible: boolean;
} & InitialPlacement) {
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const theme = useTheme();
  const { t } = useTranslation();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileWithPreview[]>(
    []
  );
  const [error, setError] = useState<null | string>(null);

  const errorMessage = error
    ? error === BackErrors.FolderExists
      ? `Загрузка запрещена. Обнаружена папка с${nbsp}таким${nbsp}же наименованием. Переименуйте папку или измените локацию для загрузки и${nbsp}попробуйте снова`
      : 'Не удалось загрузить'
    : null;

  const rootFolder = useMemo(() => {
    const originalFiles = map(uploadedFiles, (file) => file.originalFile);
    return FolderNodeModel.constructTree(originalFiles);
  }, [uploadedFiles]);

  const allFiles = rootFolder?.allFiles || [];

  useEffect(() => {
    if (allFiles.length === 0) {
      setUploadedFiles([]);
    }
  }, [allFiles.length]);

  const gridSx: SxProps = isVisible
    ? allFiles.length
      ? {
          'gridTemplateAreas': "'left right' 'bottom bottom'",
          '& > :nth-of-type(1)': {
            gridArea: 'left',
          },
          '& > :nth-of-type(2)': {
            gridArea: 'right',
          },
          '& > :nth-of-type(3)': {
            gridArea: 'bottom',
          },
        }
      : {
          'gridTemplateAreas': "'bottom bottom'",
          '& > :nth-of-type(1)': {
            gridArea: 'bottom',
          },
        }
    : {
        visibility: 'hidden',
        minHeight: 0,
        maxHeight: 0,
        height: 0,
        overflow: 'hidden',
      };

  return (
    <UploadGrid sx={{ ...gridSx, height: 1 }}>
      {rootFolder ? (
        <TreeContainer error={errorMessage}>
          <FolderNodeComponent
            onDelete={() => {
              setUploadedFiles([]);
            }}
            level={0}
            model={rootFolder}
          ></FolderNodeComponent>
        </TreeContainer>
      ) : null}

      <CreateFileController
        uploadModalText="Загрузка папки"
        getFiles={async () => {
          let result:
            | UploadedFileWithPreview[]
            | Promise<UploadedFileWithPreview[]> = [];

          if (rootFolder) {
            result = rootFolder.getArchieve();
          }

          return result;
        }}
        isOnlyModal={allFiles.length === 0}
        onFileSuccess={() => setUploadedFiles([])}
        onFileError={(name, error) => {
          setError(error);
        }}
        saveBlockProps={{
          initialSectionId,
          initialFolderId,
          initialName,
          onCancel: () => {
            setUploadedFiles([]);
            setError(null);
          },
        }}
        onUpload={filesApi.uploadFolder}
      ></CreateFileController>

      {allFiles.length ? null : (
        <UploadFolderArea
          innerSx={{ height: 550 }}
          text={t('noCategory.uploadFolderHelp')}
          buttonText={t('actions.folderUpload')}
          icon={
            <IconFolder
              sizeKey="48x48"
              extColor={theme.palette.text.secondary}
            />
          }
          handleAddFile={(newFile) => {
            setError(null);
            setUploadedFiles((curr) => {
              if (isFileIgnored(newFile.originalFile.name)) {
                return curr;
              }

              each(curr, (file) => {
                if (file.originalFile.name === newFile.originalFile.name) {
                  return curr;
                }
              });

              return [...curr, newFile];
            });
          }}
        ></UploadFolderArea>
      )}
    </UploadGrid>
  );
});
